import React, { useState } from 'react';
import {
  X,
  ShieldCheck,
  Crown,
  Calendar,
  Clock,
  Wallet,
  Eye,
  MessageSquare,
  User,
  Settings,
  AlertCircle,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  TrendingUp,
  CreditCard,
  RotateCcw,
  Star,
  Check,
  ChevronRight,
  HelpCircle,
  FileText,
  RefreshCw,
  Lock,
  Camera,
  Upload,
  MapPin,
  ExternalLink,
} from 'lucide-react';
import {
  CompanionStatus,
  CompanionMembershipPlan,
  MembershipSubscription,
  UserProfile,
  CompanionProfileStatus,
  MembershipStatus,
  PaymentStatus,
} from '../types';
import {
  verifyPaymentWithBackend,
  fetchCompanionServerStatus,
  createPaymentOrder,
} from '../lib/paymentService';
import { CompanionPhotoGallery } from './CompanionPhotoGallery';
import { DocumentUploadModal } from './DocumentUploadModal';
import { FaceVerificationModal } from './FaceVerificationModal';
import { getRealMeetups } from '../utils/meetupStorage';
import { RealMeetup } from '../types';
import { CompanionProfileStatusIndicator } from './dashboard/CompanionProfileStatusIndicator';
import { auth } from '../lib/firebase';

interface CompanionDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  userProfile: UserProfile | null;
  onUpdateProfile?: (updated: Partial<UserProfile>) => void;
  onOpenMembershipModal?: () => void;
  onOpenProfileEditor?: () => void;
}

export const CompanionDashboardModal: React.FC<CompanionDashboardModalProps> = ({
  isOpen,
  onClose,
  userProfile,
  onUpdateProfile,
  onOpenMembershipModal,
  onOpenProfileEditor,
}) => {
  const [activeTab, setActiveTab] = useState<
    'overview' | 'bookings' | 'requests' | 'messages' | 'profile' | 'earnings' | 'membership' | 'verification' | 'settings'
  >('overview');

  // Cancel Membership Modal State
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelConfirmed, setCancelConfirmed] = useState(false);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [selectedNewPlan, setSelectedNewPlan] = useState<CompanionMembershipPlan | null>(null);

  // Authoritative Backend Activation State
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [verificationFeedback, setVerificationFeedback] = useState<string | null>(null);
  const [showDirectPaySection, setShowDirectPaySection] = useState(false);

  // Identity Verification Modals (Camera & File / Selfie Auto-Verify)
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [isSelfieModalOpen, setIsSelfieModalOpen] = useState(false);

  // Real client meetups for companion
  const [realMeetups] = useState<RealMeetup[]>(() => getRealMeetups());

  if (!isOpen) return null;

  const companionName = userProfile?.companionProfile?.displayName || userProfile?.name || 'Companion Host';
  const companionStatus: CompanionStatus = userProfile?.companionStatus || 'IDENTITY_VERIFICATION_PENDING';
  const membership = userProfile?.membership || {
    plan: 'PROFESSIONAL',
    price: 1099,
    status: 'PENDING',
    nextBillingDate: 'Not active',
    paymentHistory: [],
  };

  const isVerified = userProfile?.isIdVerified === true;
  const isProfileComplete = Boolean(userProfile?.companionProfile?.displayName);

  // AUTHORITATIVE BACKEND ACTIVATION RULE:
  // IF companion_payment_status == "Success" AND payment_verified == true
  // THEN companion_profile_status = "Active" ELSE "Inactive"
  const isPaymentVerified = userProfile?.payment_verified === true;
  const isPaymentSuccess = userProfile?.payment_status === 'Success';
  const isProfileActive =
    userProfile?.companion_profile_status === 'Active' &&
    isPaymentVerified &&
    isPaymentSuccess;

  const currentProfileStatus: CompanionProfileStatus = isProfileActive ? 'Active' : 'Inactive';
  const currentPaymentStatus: PaymentStatus =
    userProfile?.payment_status || (userProfile?.membership?.status === 'ACTIVE' ? 'Success' : 'Pending');

  const isFullyActive = isVerified && isProfileActive && isProfileComplete;

  const handleCheckLiveServerStatus = async () => {
    setIsVerifyingPayment(true);
    const companionId = auth.currentUser?.uid || userProfile?.uid || '';
    if (!companionId) {
      setIsVerifyingPayment(false);
      setVerificationFeedback('Please sign in with your authenticated companion account first.');
      return;
    }
    try {
      const serverStatus = await fetchCompanionServerStatus(companionId);
      setIsVerifyingPayment(false);
      if (serverStatus) {
        setVerificationFeedback(
          `Server: Status=${serverStatus.companion_profile_status}, Payment=${serverStatus.payment_status}, Verified=${serverStatus.payment_verified ? 'Yes' : 'No'}`
        );
        if (onUpdateProfile) {
          onUpdateProfile({
            companion_profile_status: serverStatus.companion_profile_status,
            membership_status: serverStatus.membership_status,
            payment_status: serverStatus.payment_status,
            payment_verified: serverStatus.payment_verified,
            companionStatus: serverStatus.companion_profile_status === 'Active' ? 'ACTIVE' : 'MEMBERSHIP_PENDING',
          });
        }
      } else {
        setVerificationFeedback('Payment not yet verified on server. Complete Razorpay checkout to activate.');
      }
    } catch {
      setIsVerifyingPayment(false);
      setVerificationFeedback('Unable to reach verification server.');
    }
  };

  const handleOpenRealCheckout = () => {
    if (onOpenMembershipModal) {
      onOpenMembershipModal();
    } else {
      setActiveTab('membership');
    }
  };

  const handleConfirmCancelMembership = () => {
    if (onUpdateProfile) {
      onUpdateProfile({
        membership: {
          ...membership,
          status: 'CANCELLED',
        },
      });
    }
    setCancelConfirmed(true);
    setTimeout(() => {
      setShowCancelModal(false);
      setCancelConfirmed(false);
    }, 2000);
  };

  const handleSelectNewPlan = (newPlan: CompanionMembershipPlan, newPrice: number) => {
    if (onUpdateProfile) {
      onUpdateProfile({
        membership: {
          ...membership,
          plan: newPlan,
          price: newPrice,
          status: isProfileActive ? membership.status : 'PENDING',
        },
      });
    }
    setIsChangingPlan(false);
    if (onOpenMembershipModal) {
      onOpenMembershipModal();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 animate-in fade-in">
      <div className="relative bg-white rounded-3xl max-w-4xl w-full overflow-hidden shadow-2xl border border-pink-100 max-h-[92vh] flex flex-col">
        
        {/* Header Section */}
        <div className="p-5 sm:p-6 bg-gradient-to-r from-slate-900 via-purple-950 to-slate-900 text-white flex flex-wrap items-center justify-between gap-4 border-b border-purple-900/40">
          <div className="flex items-center gap-3.5">
            <div className="relative">
              <img
                src={
                  userProfile?.avatarUrl ||
                  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80'
                }
                alt={companionName}
                className="w-13 h-13 rounded-2xl object-cover border-2 border-pink-400 shadow-md"
              />
              <span
                className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-slate-900 ${
                  isFullyActive ? 'bg-emerald-500' : 'bg-amber-500'
                }`}
                title={isFullyActive ? 'Online & Bookable' : 'Setup Incomplete'}
              />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg sm:text-xl font-extrabold text-white">
                  Welcome, {companionName} 👋
                </h2>
                <CompanionProfileStatusIndicator
                  userProfile={userProfile}
                  onNavigateToVerification={() => setActiveTab('verification')}
                  compact
                />
              </div>
              <p className="text-xs text-purple-200/80 mt-0.5 flex items-center gap-2">
                <span className="font-semibold text-pink-300">
                  {membership.plan} Companion Member
                </span>
                <span>•</span>
                <span>Next payment: {membership.nextBillingDate}</span>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Authoritative Companion Inactive Banner: "Complete Payment to Activate Profile" */}
        {!isProfileActive && (
          <div className="bg-gradient-to-r from-rose-50 via-amber-50 to-rose-50 border-b-2 border-rose-300 p-4 sm:px-6 text-rose-950 text-xs shadow-inner">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-rose-500 text-white rounded-xl mt-0.5 shadow-xs">
                  <Lock className="w-5 h-5 shrink-0" />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-black text-rose-900 tracking-tight">
                      Complete Payment to Activate Profile
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-200 text-rose-900 border border-rose-300">
                      🔴 Status: INACTIVE
                    </span>
                    <span className="text-[10px] font-bold text-slate-500">
                      (Payment: {currentPaymentStatus} • Verified: {isPaymentVerified ? 'Yes' : 'No'})
                    </span>
                  </div>
                  <p className="text-[11px] text-rose-800 leading-relaxed max-w-2xl">
                    Per security and platform rules, your profile is <strong>INACTIVE</strong> until payment is verified by the backend gateway:
                  </p>
                  <div className="flex flex-wrap items-center gap-2 pt-0.5 text-[11px] font-semibold text-rose-900">
                    <span className="px-2 py-0.5 bg-white/80 rounded-md border border-rose-200">
                      🚫 Search/Listing: Hidden
                    </span>
                    <span className="px-2 py-0.5 bg-white/80 rounded-md border border-rose-200">
                      🚫 Booking Requests: Blocked
                    </span>
                    <span className="px-2 py-0.5 bg-white/80 rounded-md border border-rose-200">
                      🚫 Earnings & Escrow: Locked
                    </span>
                  </div>
                  {verificationFeedback && (
                    <div className="text-[11px] font-bold text-slate-800 bg-white px-3 py-1.5 rounded-xl border border-rose-300 inline-flex items-center gap-1.5 mt-1 shadow-xs">
                      <RefreshCw className="w-3.5 h-3.5 text-purple-600 animate-spin" />
                      <span>{verificationFeedback}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 shrink-0 self-start lg:self-center">
                <button
                  type="button"
                  disabled={isVerifyingPayment}
                  onClick={handleOpenRealCheckout}
                  className="px-3.5 py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-bold text-xs rounded-xl shadow-sm transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  <span>{`Pay ₹${membership.price} to Activate`}</span>
                </button>

                <button
                  type="button"
                  disabled={isVerifyingPayment}
                  onClick={handleCheckLiveServerStatus}
                  className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                  title="Check backend gateway verification of payment"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isVerifyingPayment ? 'animate-spin' : ''}`} />
                  <span>{isVerifyingPayment ? 'Checking...' : 'Check Server Status'}</span>
                </button>
              </div>
            </div>

            {/* Inline Quick Payment Section */}
            {showDirectPaySection && (
              <div className="mt-4 pt-4 border-t border-rose-200/80 bg-white p-4 rounded-2xl border border-rose-200 space-y-3">
                <div className="p-3.5 bg-purple-50 rounded-2xl border border-purple-200 text-purple-900 space-y-1">
                  <p className="font-bold text-xs flex items-center gap-1.5 text-purple-950">
                    <ShieldCheck className="w-4 h-4 text-purple-600" />
                    Backend Activation Rule
                  </p>
                  <p className="text-[11px] text-purple-800 leading-normal">
                    Profile is activated <strong>ONLY</strong> after backend verification confirms <code className="bg-purple-100 px-1 py-0.5 rounded text-purple-900 font-mono text-[10px]">payment_status == &quot;Success&quot;</code> and <code className="bg-purple-100 px-1 py-0.5 rounded text-purple-900 font-mono text-[10px]">payment_verified == true</code>.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={isVerifyingPayment}
                    onClick={handleOpenRealCheckout}
                    className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-md cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    <span>{`Proceed to Razorpay Checkout (₹${membership.price})`}</span>
                  </button>
                  <button
                    type="button"
                    disabled={isVerifyingPayment}
                    onClick={handleCheckLiveServerStatus}
                    className="py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    <span>Check Live Server Status</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50/70 overflow-x-auto px-4 sm:px-6 scrollbar-none">
          {[
            { id: 'overview', label: 'Overview', icon: TrendingUp },
            { id: 'requests', label: 'Booking Requests', icon: Calendar, badge: '0' },
            { id: 'bookings', label: 'My Bookings', icon: Clock },
            { id: 'messages', label: 'Messages', icon: MessageSquare },
            { id: 'earnings', label: 'Earnings', icon: Wallet },
            { id: 'membership', label: 'Membership', icon: Crown },
            { id: 'verification', label: 'Verification', icon: ShieldCheck },
            { id: 'profile', label: 'My Profile', icon: User },
            { id: 'settings', label: 'Settings', icon: Settings },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-3.5 px-3 sm:px-4 text-xs font-bold whitespace-nowrap flex items-center gap-1.5 border-b-2 transition-all cursor-pointer ${
                  isActive
                    ? 'border-pink-600 text-pink-600 bg-white/60 font-extrabold'
                    : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-pink-600' : 'text-slate-400'}`} />
                <span>{tab.label}</span>
                {tab.badge && (
                  <span className="px-1.5 py-0.2 bg-slate-200 text-slate-700 text-[10px] rounded-full font-bold">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Dashboard Main Content Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-7 space-y-6">
          
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="space-y-6 animate-in fade-in">
              {/* Visual Profile & Document Validation Status Indicator */}
              <CompanionProfileStatusIndicator
                userProfile={userProfile}
                onNavigateToVerification={() => setActiveTab('verification')}
              />

              {/* 4 Metric Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4">
                  <div className="flex items-center justify-between text-slate-500 mb-2">
                    <span className="text-xs font-semibold">Today's Bookings</span>
                    <Calendar className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="text-2xl font-black text-slate-900">0</div>
                  <span className="text-[10px] text-slate-400 mt-1 block">Scheduled for today</span>
                </div>

                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4">
                  <div className="flex items-center justify-between text-slate-500 mb-2">
                    <span className="text-xs font-semibold">Upcoming Bookings</span>
                    <Clock className="w-4 h-4 text-pink-600" />
                  </div>
                  <div className="text-2xl font-black text-slate-900">0</div>
                  <span className="text-[10px] text-slate-400 mt-1 block">Confirmed reservations</span>
                </div>

                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4">
                  <div className="flex items-center justify-between text-slate-500 mb-2">
                    <span className="text-xs font-semibold">This Month's Earnings</span>
                    <Wallet className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="text-2xl font-black text-emerald-600">
                    ₹{userProfile?.wallet?.availableBalance?.toLocaleString('en-IN') || '0'}
                  </div>
                  <span className="text-[10px] text-emerald-600 font-medium mt-1 block">Instant payout ready</span>
                </div>

                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4">
                  <div className="flex items-center justify-between text-slate-500 mb-2">
                    <span className="text-xs font-semibold">Profile Views</span>
                    <Eye className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-2xl font-black text-slate-900">0</div>
                  <span className="text-[10px] text-slate-400 font-medium mt-1 block">Live metrics tracked on activation</span>
                </div>
              </div>

              {/* Status & Next Steps Card */}
              <div className="p-5 rounded-3xl border border-purple-100 bg-gradient-to-r from-purple-50/60 via-pink-50/40 to-white">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <span className="px-2.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-extrabold rounded-full uppercase tracking-wider">
                      Onboarding Pipeline
                    </span>
                    <h3 className="text-base font-extrabold text-slate-900">
                      Companion Activation Status: {isFullyActive ? 'Active & Receiving Requests' : 'Pending Requirements'}
                    </h3>
                    <p className="text-xs text-slate-600">
                      {isFullyActive
                        ? 'Your profile is live, verified, and featured on the client search directory.'
                        : 'Complete all steps below to unlock client booking requests and earnings.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setActiveTab('profile')}
                      className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-800 font-bold text-xs rounded-xl border border-slate-200 transition-colors cursor-pointer"
                    >
                      Edit Profile
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('membership')}
                      className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
                    >
                      Manage Membership
                    </button>
                  </div>
                </div>

                {/* 3 Pillars checklist */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-purple-100 text-xs">
                  <div className="flex items-center gap-2.5">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs ${
                        isVerified ? 'bg-emerald-500' : 'bg-amber-500'
                      }`}
                    >
                      {isVerified ? '✓' : '•'}
                    </div>
                    <div>
                      <strong className="block text-slate-800">Govt ID Verification</strong>
                      <span className="text-[11px] text-slate-500">{isVerified ? 'Verified' : 'In Review'}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs ${
                        isProfileActive ? 'bg-emerald-500' : 'bg-rose-500'
                      }`}
                    >
                      {isProfileActive ? '✓' : '!'}
                    </div>
                    <div>
                      <strong className="block text-slate-800">Membership Active</strong>
                      <span className="text-[11px] text-slate-500">
                        {isProfileActive ? `${membership.plan} Tier` : 'Payment Required'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs ${
                        isProfileComplete ? 'bg-emerald-500' : 'bg-amber-500'
                      }`}
                    >
                      {isProfileComplete ? '✓' : '•'}
                    </div>
                    <div>
                      <strong className="block text-slate-800">Profile & Rates</strong>
                      <span className="text-[11px] text-slate-500">
                        ₹{userProfile?.hourlyRate || 1500}/hr configured
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions Shortcuts */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Quick Actions</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <button
                    onClick={() => setActiveTab('requests')}
                    className="p-3 bg-white border border-slate-200 hover:border-pink-300 rounded-2xl text-left transition-all group cursor-pointer shadow-2xs"
                  >
                    <Calendar className="w-5 h-5 text-pink-600 mb-2 group-hover:scale-110 transition-transform" />
                    <span className="font-bold text-xs text-slate-900 block">Booking Requests</span>
                    <span className="text-[10px] text-slate-500">Review pending clients</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('earnings')}
                    className="p-3 bg-white border border-slate-200 hover:border-emerald-300 rounded-2xl text-left transition-all group cursor-pointer shadow-2xs"
                  >
                    <Wallet className="w-5 h-5 text-emerald-600 mb-2 group-hover:scale-110 transition-transform" />
                    <span className="font-bold text-xs text-slate-900 block">Earnings & Payouts</span>
                    <span className="text-[10px] text-slate-500">Instant UPI withdrawals</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('membership')}
                    className="p-3 bg-white border border-slate-200 hover:border-purple-300 rounded-2xl text-left transition-all group cursor-pointer shadow-2xs"
                  >
                    <Crown className="w-5 h-5 text-purple-600 mb-2 group-hover:scale-110 transition-transform" />
                    <span className="font-bold text-xs text-slate-900 block">My Membership</span>
                    <span className="text-[10px] text-slate-500">Plan details & renewal</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('verification')}
                    className="p-3 bg-white border border-slate-200 hover:border-blue-300 rounded-2xl text-left transition-all group cursor-pointer shadow-2xs"
                  >
                    <ShieldCheck className="w-5 h-5 text-blue-600 mb-2 group-hover:scale-110 transition-transform" />
                    <span className="font-bold text-xs text-slate-900 block">ID Verification</span>
                    <span className="text-[10px] text-slate-500">Trust badge status</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: MEMBERSHIP MANAGEMENT PAGE (SECTION 15 OF SPEC) */}
          {activeTab === 'membership' && (
            <div className="space-y-6 animate-in fade-in">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-xl font-extrabold text-slate-900">My Membership</h3>
                  <p className="text-xs text-slate-500">
                    Authoritative server-side payment verification & companion subscription management.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider flex items-center gap-1.5 w-fit ${
                      isProfileActive ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${isProfileActive ? 'bg-emerald-600' : 'bg-rose-600'}`} />
                    {isProfileActive ? '🟢 Profile: Active' : '🔴 Profile: Inactive'}
                  </span>
                </div>
              </div>

              {/* Server Activation Rule Card */}
              <div className={`p-4 sm:p-5 rounded-3xl border ${isProfileActive ? 'bg-emerald-50/60 border-emerald-200 text-emerald-950' : 'bg-rose-50/70 border-rose-300 text-rose-950'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className={`w-5 h-5 ${isProfileActive ? 'text-emerald-600' : 'text-rose-600'}`} />
                      <strong className="text-sm font-black">
                        {isProfileActive ? 'Backend Payment Verified — Profile is Active' : 'Payment Verification Required to Activate Profile'}
                      </strong>
                    </div>
                    <p className="text-xs opacity-90 leading-relaxed font-mono">
                      Rule: IF payment_status == &quot;Success&quot; AND payment_verified == true THEN Profile = &quot;Active&quot; ELSE &quot;Inactive&quot;
                    </p>
                    <div className="flex flex-wrap items-center gap-2 pt-1 text-[11px] font-bold">
                      <span className="bg-white/80 px-2 py-0.5 rounded-md border border-black/10">
                        Profile Status: {currentProfileStatus}
                      </span>
                      <span className="bg-white/80 px-2 py-0.5 rounded-md border border-black/10">
                        Payment: {currentPaymentStatus}
                      </span>
                      <span className="bg-white/80 px-2 py-0.5 rounded-md border border-black/10">
                        Gateway Verified: {isPaymentVerified ? 'true ✓' : 'false ✗'}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <button
                      type="button"
                      disabled={isVerifyingPayment}
                      onClick={handleOpenRealCheckout}
                      className="px-3.5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>{`Checkout via Razorpay (₹${membership.price})`}</span>
                    </button>
                    <button
                      type="button"
                      disabled={isVerifyingPayment}
                      onClick={handleCheckLiveServerStatus}
                      className="p-2 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 rounded-xl cursor-pointer"
                      title="Sync from Authoritative Server"
                    >
                      <RefreshCw className={`w-4 h-4 ${isVerifyingPayment ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>

                {verificationFeedback && (
                  <div className="mt-3 text-[11px] font-bold text-slate-900 bg-white p-2.5 rounded-xl border border-black/10 shadow-xs">
                    Server Response: {verificationFeedback}
                  </div>
                )}
              </div>

              {/* Current Plan Card */}
              <div className="p-6 rounded-3xl border border-purple-200 bg-gradient-to-br from-purple-50/70 via-pink-50/40 to-white shadow-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-purple-700 bg-purple-100 px-2.5 py-0.5 rounded-full">
                      Current Subscription
                    </span>
                    <h4 className="text-2xl font-black text-slate-900 mt-2">
                      {membership.plan}
                    </h4>
                    <p className="text-xs text-slate-600 mt-1">
                      Full access to client booking requests, earnings dashboard, and priority profile placement.
                    </p>
                  </div>
                  <div className="text-left sm:text-right">
                    <div className="text-3xl font-black text-slate-900">
                      ₹{membership.price}
                      <span className="text-xs font-normal text-slate-500">/plan</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Next Billing Date: <strong className="text-slate-800">{membership.nextBillingDate}</strong>
                    </p>
                  </div>
                </div>

                {/* Membership Action Buttons */}
                <div className="flex flex-wrap items-center gap-2.5 mt-6 pt-5 border-t border-purple-200/70">
                  <button
                    type="button"
                    onClick={() => setIsChangingPlan(true)}
                    className="px-4 py-2.5 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
                  >
                    Change Plan
                  </button>

                  <button
                    type="button"
                    onClick={handleOpenRealCheckout}
                    className="px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs rounded-xl border border-slate-300 transition-colors cursor-pointer flex items-center gap-1.5"
                  >
                    <CreditCard className="w-3.5 h-3.5 text-purple-600" />
                    <span>Pay via Razorpay</span>
                  </button>

                  {isProfileActive && (
                    <button
                      type="button"
                      onClick={() => setShowCancelModal(true)}
                      className="px-4 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs rounded-xl border border-rose-200 transition-colors cursor-pointer ml-auto"
                    >
                      Cancel Membership
                    </button>
                  )}
                </div>
              </div>

              {/* Change Plan Selector if toggled */}
              {isChangingPlan && (
                <div className="p-5 border border-slate-300 rounded-3xl bg-slate-50 space-y-4 animate-in fade-in">
                  <div className="flex items-center justify-between">
                    <h4 className="font-extrabold text-sm text-slate-900">Switch Companion Membership Plan</h4>
                    <button
                      onClick={() => setIsChangingPlan(false)}
                      className="text-xs text-slate-500 hover:text-slate-900 font-bold"
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                      {
                        plan: 'STARTER' as const,
                        price: 449,
                        durationLabel: '/ mo',
                        tag: 'Basic',
                        desc: 'Receive Client Requests & Basic Visibility',
                      },
                      {
                        plan: 'PROFESSIONAL' as const,
                        price: 1099,
                        durationLabel: '/ 3 mo',
                        tag: 'Most Popular',
                        desc: 'Higher Visibility & AI Matching',
                      },
                      {
                        plan: 'PREMIUM' as const,
                        price: 5999,
                        durationLabel: '/ yr',
                        tag: 'Featured',
                        desc: 'Featured Placement & Priority Support',
                      },
                    ].map((p) => (
                      <div
                        key={p.plan}
                        className={`p-4 rounded-2xl bg-white border text-left transition-all ${
                          membership.plan === p.plan ? 'border-pink-500 ring-2 ring-pink-500/20' : 'border-slate-200'
                        }`}
                      >
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-black text-slate-900 text-sm">{p.plan}</span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                            {p.tag}
                          </span>
                        </div>
                        <p className="text-xl font-black text-slate-900 mb-1">₹{p.price}{p.durationLabel}</p>
                        <p className="text-[11px] text-slate-500 mb-3">{p.desc}</p>
                        <button
                          disabled={membership.plan === p.plan}
                          onClick={() => handleSelectNewPlan(p.plan, p.price)}
                          className="w-full py-1.5 text-xs font-bold rounded-lg bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50 cursor-pointer"
                        >
                          {membership.plan === p.plan ? 'Current Plan' : `Switch to ${p.plan}`}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment History Section */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <CreditCard className="w-4 h-4 text-purple-600" />
                  Subscription Payment History
                </h4>
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-2xl overflow-hidden bg-white">
                  {membership.paymentHistory.map((item) => (
                    <div key={item.id} className="p-3.5 sm:p-4 flex items-center justify-between text-xs">
                      <div>
                        <strong className="text-slate-900 font-bold block">{item.plan} Membership</strong>
                        <span className="text-slate-400 text-[11px]">Txn ID: {item.id} • {item.date}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-black text-slate-900 block">₹{item.amount}</span>
                        <span className={`font-bold text-[10px] ${item.status === 'PAID' ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {item.status === 'PAID' ? '✓ PAID' : '✗ FAILED'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: BOOKING REQUESTS */}
          {activeTab === 'requests' && (
            <div className="space-y-4 animate-in fade-in">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-extrabold text-slate-900">Incoming Client Requests</h3>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${isProfileActive ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                  {isProfileActive ? 'Booking Status: Unlocked' : 'Booking Status: Locked (Profile Inactive)'}
                </span>
              </div>

              {!isProfileActive ? (
                <div className="p-8 text-center bg-rose-50/70 rounded-3xl border-2 border-dashed border-rose-300 space-y-3">
                  <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto">
                    <Lock className="w-6 h-6" />
                  </div>
                  <h4 className="font-extrabold text-rose-950 text-sm">
                    Booking Acceptance Locked — Profile Inactive
                  </h4>
                  <p className="text-xs text-rose-800 max-w-md mx-auto leading-relaxed">
                    You cannot receive or accept client bookings until your companion membership payment is verified by the backend gateway.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveTab('membership')}
                    className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-bold text-xs rounded-xl shadow-md cursor-pointer inline-flex items-center gap-1.5"
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>Complete Payment to Activate (₹{membership.price})</span>
                  </button>
                </div>
              ) : (
                <div className="p-8 text-center bg-slate-50 rounded-3xl border border-slate-200 space-y-2">
                  <div className="w-12 h-12 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mx-auto mb-2">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <h4 className="font-bold text-slate-800 text-sm">No Pending Requests</h4>
                  <p className="text-xs text-slate-500 max-w-sm mx-auto">
                    When clients in your city book you for events or social companionship, their detailed request will appear here for you to Accept or Decline.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* TAB 4: MY BOOKINGS */}
          {activeTab === 'bookings' && (
            <div className="space-y-4 animate-in fade-in">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-extrabold text-slate-900">My Confirmed Bookings</h3>
                <span className="text-xs font-bold text-slate-500">
                  {realMeetups.length} scheduled meetup{realMeetups.length === 1 ? '' : 's'}
                </span>
              </div>

              {realMeetups && realMeetups.length > 0 ? (
                <div className="space-y-3">
                  {realMeetups.map((m) => (
                    <div
                      key={m.id}
                      className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs space-y-3"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-extrabold text-slate-900">{m.title}</span>
                            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-mono text-[10px] font-bold">
                              #{m.bookingId}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Client: <strong className="text-slate-800">{m.clientName}</strong> • 📅 {m.date} at 🕕 {m.time} ({m.hours} hrs)
                          </p>
                        </div>
                        <span className="px-2.5 py-1 rounded-full text-xs font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          ₹{m.totalAmount} (Escrow Protected)
                        </span>
                      </div>

                      {/* Meeting Location with Direct Google Maps navigation link */}
                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                        <div className="flex items-start gap-2 min-w-0">
                          <MapPin className="w-4 h-4 text-pink-600 shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
                              Meeting Location
                            </span>
                            <p className="text-xs font-bold text-slate-900 truncate">
                              📍 {m.location}
                            </p>
                          </div>
                        </div>

                        <a
                          href={m.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(m.location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-pink-50 hover:bg-pink-100 border border-pink-200 text-pink-700 text-xs font-bold transition-all shrink-0 cursor-pointer"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          <span>Open in Google Maps</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center bg-slate-50 rounded-3xl border border-slate-200 space-y-2">
                  <div className="w-12 h-12 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center mx-auto mb-2">
                    <Clock className="w-6 h-6" />
                  </div>
                  <h4 className="font-bold text-slate-800 text-sm">No Upcoming Meetups</h4>
                  <p className="text-xs text-slate-500 max-w-sm mx-auto">
                    Once you accept client requests, your schedule, meeting location, and client verification OTP will appear here.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* TAB 5: EARNINGS & PAYOUTS */}
          {activeTab === 'earnings' && (
            <div className="space-y-6 animate-in fade-in">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-extrabold text-slate-900">Companion Earnings & Escrow</h3>
                  <p className="text-xs text-slate-500">100% of client meetup payments are released instantly upon OTP verification.</p>
                </div>
                <span className={`text-2xl font-black ${isProfileActive ? 'text-emerald-600' : 'text-slate-400'}`}>
                  ₹{userProfile?.wallet?.availableBalance?.toLocaleString('en-IN') || '0'}
                </span>
              </div>

              {!isProfileActive ? (
                <div className="p-8 text-center bg-rose-50/70 rounded-3xl border-2 border-dashed border-rose-300 space-y-3">
                  <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto">
                    <Lock className="w-6 h-6" />
                  </div>
                  <h4 className="font-extrabold text-rose-950 text-sm">
                    Earnings & Provider Features Locked
                  </h4>
                  <p className="text-xs text-rose-800 max-w-md mx-auto leading-relaxed">
                    Provider earnings features, instant UPI bank transfers, and client escrow releases are locked while your profile is <strong>Inactive</strong>. Complete payment verification to unlock.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveTab('membership')}
                    className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-bold text-xs rounded-xl shadow-md cursor-pointer inline-flex items-center gap-1.5"
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>Complete Payment to Activate</span>
                  </button>
                </div>
              ) : (
                <div className="p-5 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-3xl border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h4 className="font-bold text-emerald-950 text-sm">Instant UPI Bank Transfer</h4>
                    <p className="text-xs text-emerald-800">
                      Configured UPI: <span className="font-mono font-bold">{userProfile?.upiId || 'Not Configured'}</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => alert('Your wallet balance is currently ₹0. Complete bookings to withdraw earnings.')}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-xs transition-colors cursor-pointer"
                  >
                    Withdraw to Bank
                  </button>
                </div>
              )}
            </div>
          )}

          {/* TAB 6: ID VERIFICATION */}
          {activeTab === 'verification' && (
            <div className="space-y-5 animate-in fade-in">
              <CompanionProfileStatusIndicator
                userProfile={userProfile}
                onNavigateToVerification={() => {}}
              />

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-extrabold text-slate-900">Government Identity Verification</h3>
                  <p className="text-xs text-slate-500">Mandatory for all active companions to ensure client and host safety.</p>
                </div>
                {isVerified ? (
                  <span className="px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                    Verified & Approved
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-amber-100 text-amber-800 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-amber-600" />
                    Pending Admin KYC Review
                  </span>
                )}
              </div>

              {/* Verified Documents & Selfie Previews */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* ID Document Card */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-3xl space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                      <FileText className="w-4 h-4 text-purple-600" />
                      {userProfile?.companionProfile?.idType || 'Aadhaar Card'}
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${userProfile?.companionProfile?.idDocumentUrl ? 'text-emerald-700 bg-emerald-100' : 'text-amber-700 bg-amber-100'}`}>
                      {userProfile?.companionProfile?.idDocumentUrl ? 'Attached ✓' : 'Not Uploaded'}
                    </span>
                  </div>

                  <div className="aspect-[16/10] bg-slate-200/70 rounded-2xl overflow-hidden flex items-center justify-center border border-slate-300/50">
                    {userProfile?.companionProfile?.idDocumentUrl && userProfile.companionProfile.idDocumentUrl !== 'pdf_placeholder' ? (
                      <img
                        src={userProfile.companionProfile.idDocumentUrl}
                        alt="Govt ID Document"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center p-3">
                        <FileText className="w-8 h-8 text-purple-600 mx-auto mb-1" />
                        <span className="text-[11px] font-bold text-slate-700 block">
                          {userProfile?.companionProfile?.idNumber ? 'Document Submitted' : 'No Document Submitted'}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {userProfile?.companionProfile?.idNumber || 'Not submitted'}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500 text-[11px]">Number:</span>
                    <span className="font-mono font-bold text-slate-800 text-[11px]">
                      {userProfile?.companionProfile?.idNumber || 'Not submitted'}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsDocModalOpen(true)}
                    className="w-full py-2 bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                  >
                    <Upload className="w-3.5 h-3.5 text-purple-600" />
                    <span>Update ID (Camera / File)</span>
                  </button>
                </div>

                {/* Biometric Selfie Card */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-3xl space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                      <Camera className="w-4 h-4 text-purple-600" />
                      Verification Selfie
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isVerified ? 'text-emerald-700 bg-emerald-100' : 'text-amber-700 bg-amber-100'}`}>
                      {isVerified ? 'Approved ✓' : 'Pending KYC Review'}
                    </span>
                  </div>

                  <div className="aspect-[16/10] bg-slate-200/70 rounded-2xl overflow-hidden flex items-center justify-center border border-slate-300/50">
                    {userProfile?.companionProfile?.selfieUrl || userProfile?.avatarUrl ? (
                      <img
                        src={userProfile?.companionProfile?.selfieUrl || userProfile?.avatarUrl}
                        alt="Verification Selfie"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center p-3">
                        <Camera className="w-8 h-8 text-purple-600 mx-auto mb-1" />
                        <span className="text-[11px] font-bold text-slate-700 block">No Selfie Uploaded</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1 text-xs">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-slate-500">Review Mode:</span>
                      <span className="font-bold text-slate-700">Admin KYC Desk</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-slate-500">Status:</span>
                      <span className={`font-bold ${isVerified ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {isVerified ? 'Verified ✓' : 'Awaiting Review'}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsSelfieModalOpen(true)}
                    className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Camera className="w-3.5 h-3.5" />
                    <span>Capture Verification Selfie</span>
                  </button>
                </div>
              </div>

              <div className="p-4 bg-purple-50/50 border border-purple-100 rounded-2xl text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Verification Process:</span>
                  <span className="font-bold text-purple-700">Document & Selfie Review by Admin KYC Desk</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Identity Status:</span>
                  <span className={`font-bold ${isVerified ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {isVerified ? 'Verified' : 'Pending Verification'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* TAB 7: PROFILE & SERVICES */}
          {activeTab === 'profile' && (
            <div className="space-y-5 animate-in fade-in">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-extrabold text-slate-900">Public Profile & Hourly Rates</h3>
                  <p className="text-xs text-slate-500">This is what clients see when browsing companion profiles.</p>
                </div>
                <button
                  type="button"
                  onClick={() => onOpenProfileEditor?.()}
                  className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-colors cursor-pointer"
                >
                  Edit Profile & Photos
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                  <span className="text-slate-400 font-bold block">Display Name</span>
                  <p className="font-bold text-slate-900 text-sm">{companionName}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                  <span className="text-slate-400 font-bold block">Hourly Rate</span>
                  <p className="font-bold text-pink-600 text-sm">₹{userProfile?.hourlyRate || 1500} / hour</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                  <span className="text-slate-400 font-bold block">City & Coverage</span>
                  <p className="font-bold text-slate-900 text-sm">{userProfile?.city || 'Kolkata, West Bengal'}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                  <span className="text-slate-400 font-bold block">Bio</span>
                  <p className="text-slate-600 text-xs line-clamp-2">
                    {userProfile?.bio || 'Verified companion host for dinner dates and social events.'}
                  </p>
                </div>
              </div>

              {/* Interactive 6-Photo Gallery */}
              <CompanionPhotoGallery
                avatarUrl={userProfile?.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80'}
                gallery={userProfile?.gallery || [userProfile?.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80']}
                userName={companionName}
                isIdVerified={isVerified}
                onUpdateGallery={(newGallery, newAvatar) => {
                  onUpdateProfile?.({
                    gallery: newGallery,
                    avatarUrl: newAvatar || userProfile?.avatarUrl,
                  });
                }}
                onShowToast={(msg) => {
                  setVerificationFeedback(msg);
                  setTimeout(() => setVerificationFeedback(null), 3500);
                }}
              />
            </div>
          )}

          {/* TAB 8: MESSAGES */}
          {activeTab === 'messages' && (
            <div className="p-8 text-center bg-slate-50 rounded-3xl border border-slate-200 space-y-2 animate-in fade-in">
              <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mx-auto mb-2">
                <MessageSquare className="w-6 h-6" />
              </div>
              <h4 className="font-bold text-slate-800 text-sm">No Active Chats</h4>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                Direct client messages will appear here once booking inquiries are confirmed.
              </p>
            </div>
          )}

          {/* TAB 9: SETTINGS */}
          {activeTab === 'settings' && (
            <div className="space-y-4 animate-in fade-in text-xs">
              <h3 className="text-base font-extrabold text-slate-900">Companion Account Settings</h3>
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-2xl bg-white">
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <strong className="font-bold text-slate-800 block">Email Notifications</strong>
                    <span className="text-slate-500 text-[11px]">Receive emails when a client sends a booking request</span>
                  </div>
                  <input type="checkbox" defaultChecked className="w-4 h-4 accent-pink-600 cursor-pointer" />
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <strong className="font-bold text-slate-800 block">SMS Alerts</strong>
                    <span className="text-slate-500 text-[11px]">Instant text alert upon urgent meetup request</span>
                  </div>
                  <input type="checkbox" defaultChecked className="w-4 h-4 accent-pink-600 cursor-pointer" />
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <strong className="font-bold text-slate-800 block">Availability Toggle</strong>
                    <span className="text-slate-500 text-[11px]">Visible on search directory for bookings</span>
                  </div>
                  <input type="checkbox" defaultChecked className="w-4 h-4 accent-pink-600 cursor-pointer" />
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div className="p-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 shrink-0">
          <span className="flex items-center gap-1.5 text-slate-600 font-medium">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            256-bit Encrypted Companion Portal
          </span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl transition-colors cursor-pointer"
          >
            Close Dashboard
          </button>
        </div>

      </div>

      {/* Cancel Membership Confirmation Dialog (Section 15 of spec) */}
      {showCancelModal && (
        <div className="fixed inset-0 z-60 overflow-y-auto bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in">
          <div className="relative bg-white rounded-3xl max-w-md w-full p-6 text-center shadow-2xl border border-slate-200">
            <div className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="w-6 h-6" />
            </div>

            <h3 className="text-lg font-extrabold text-slate-900">Cancel Membership?</h3>
            <p className="text-xs text-slate-600 mt-2 mb-6 leading-relaxed">
              Your membership will remain active until <strong>{membership.nextBillingDate}</strong>. After that, your Companion profile will no longer be able to accept new bookings unless you reactivate your membership.
            </p>

            {cancelConfirmed ? (
              <div className="p-3 bg-emerald-50 text-emerald-700 rounded-xl text-xs font-bold mb-4">
                ✓ Membership cancellation confirmed. Active until {membership.nextBillingDate}.
              </div>
            ) : null}

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer"
              >
                Keep Membership
              </button>
              <button
                type="button"
                onClick={handleConfirmCancelMembership}
                disabled={cancelConfirmed}
                className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl shadow-xs transition-colors cursor-pointer disabled:opacity-50"
              >
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Government Document Upload Modal (Camera & File) */}
      <DocumentUploadModal
        isOpen={isDocModalOpen}
        onClose={() => setIsDocModalOpen(false)}
        documentType={userProfile?.companionProfile?.idType || 'Aadhaar Card'}
        currentDocumentUrl={userProfile?.companionProfile?.idDocumentUrl}
        onDocumentUploaded={(doc) => {
          if (onUpdateProfile && userProfile?.companionProfile) {
            onUpdateProfile({
              companionProfile: {
                ...userProfile.companionProfile,
                idDocumentUrl: doc.url,
              },
            });
            setVerificationFeedback('ID Document successfully updated!');
            setTimeout(() => setVerificationFeedback(null), 3000);
          }
        }}
      />

      {/* Selfie Face Verification Modal (Live Camera, No Sunglasses, No Cap, Auto-Verify) */}
      <FaceVerificationModal
        isOpen={isSelfieModalOpen}
        onClose={() => setIsSelfieModalOpen(false)}
        currentSelfieUrl={userProfile?.companionProfile?.selfieUrl || userProfile?.avatarUrl}
        userName={companionName}
        onSelfieVerified={(selfieUrl) => {
          if (onUpdateProfile && userProfile?.companionProfile) {
            onUpdateProfile({
              avatarUrl: selfieUrl,
              companionProfile: {
                ...userProfile.companionProfile,
                selfieUrl: selfieUrl,
              },
            });
            setVerificationFeedback('Biometric selfie verified & profile photo updated!');
            setTimeout(() => setVerificationFeedback(null), 3500);
          }
        }}
      />

    </div>
  );
};
